import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client/react';
import { object, string } from 'yup';
import useForm, { UseFormOnSubmitHandler } from '@jwp/ott-hooks-react/src/useForm';
import { useMultipartUpload } from '@jwp/ott-hooks-react/src/useMultipartUpload';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { MediaCategoryReels, GET_MY_VIDEOS } from '@jwp/ott-common/src/graphql/query/reels.queries';
import { EDIT_MEDIA, EditMediaData, MediaApprovalPatch } from '@jwp/ott-common/src/graphql/mutation/reels.mutation';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { PATH_VIDEO_PREVIEW } from '@jwp/ott-common/src/paths';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import env from '@jwp/ott-common/src/env';
import { isValidMediaTagsFormat, parseUniqueMediaTags } from '@jwp/ott-common/src/utils/mediaTags';
import Spinner from '../Spinner/Spinner';
import TextField from '../TextField/TextField';
import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';
import UploadBox from '../UploadBox/UploadBox';
import ErrorIcon from '../../../../theme/assets/icons/cancel_confirm.svg';
import RestartIcon from '../../../../theme/assets/icons/restart.svg';
import styles from './UploadVideoThumbnailModal.module.scss';
import ThumbnailUserPickerModal from '../ThumbnailPickerModal/ThumbnailUserPickerModal';
import UploadCard from '../UploadCard/UploadCard';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { GET_DROPDOWNS, GetDropdownsVariables, GetDropdownsData } from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';

type Props = {
  setUploadPopupConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
};


type UploadReelFormData = {
  title: string;
  description: string;
  category: string;
  tags: string;
};

const UploadVideoThumbnailModal = ({ setUploadPopupConfirmation }: Props) => {
  const { t } = useTranslation('upload');
  const { thumbnailSizeLimit } = useAppConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    file,
    thumbnail,
    thumbnailUrl,
    previewUrl: storePreviewUrl,
    values: storeValues,
    setFile,
    setThumbnail,
    setValues,
    setReturnPath,
    mediaId,
    duration,
    setDuration,
    reset: resetStore
  } = useUploadStore();

  const isEdit = !!mediaId;
  const [isThumbnailPickerOpen, setIsThumbnailPickerOpen] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [baseValues, setBaseValues] = useState<UploadReelFormData | null>(null);
  const successRef = useRef<boolean>(false);

  const { data: dropdownData, loading: categoryLoading } = useQuery<GetDropdownsData, GetDropdownsVariables>(GET_DROPDOWNS, {
    client: getAuthApolloClient(),
    variables: { ddType: 'category' },
    errorPolicy: 'all',
  });
  const categories: MediaCategoryReels[] = useMemo(() => dropdownData?.getDropdowns?.dropdowns || [], [dropdownData]);

  const { uploadFile, updateReel, status, error: uploadError, reset: resetUpload } = useMultipartUpload();

  const [updateMediaById, { loading: updateLoading }] = useMutation<EditMediaData, { input: MediaApprovalPatch }>(EDIT_MEDIA, {
    refetchQueries: [{ query: GET_MY_VIDEOS }],
    onCompleted: () => {
      resetStore();
      navigate(createURLFromLocation(location, { u: 'edit-success' }));
    },
    onError: (error) => {
      console.error('Update failed:', error);
    },
  });

  // ✅ Improved file validation with better error handling
  const handleFileChange = (selectedFile: File | null) => {
    setFileError(null);
    setThumbnailError(null);
    if (selectedFile) {
      if (!selectedFile.type.startsWith('video/')) {
        setFileError('upload_video.errors.invalid_file_type');
        setFile(null);
        setThumbnail(null, null);
        return;
      }

      // 5GB limit
      if (selectedFile.size > 5 * 1024 * 1024 * 1024) {
        setFileError('upload_video.errors.file_size');
        setFile(null);
        setThumbnail(null, null);
        return;
      }
    }

    // ✅ Clear thumbnail when new file selected
    setThumbnail(null, null);

    setFile(selectedFile);

    // ✅ Extract video metadata
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => {
        const d = Number.isFinite(vid.duration) ? vid.duration : null;
        setVideoDuration(d);
        if (d !== null) {
          setDuration(Math.floor(d * 1000));
        }
        URL.revokeObjectURL(url);
      };
      setDuration(null);
      vid.src = url;
    } else {
      setVideoDuration(null);
      setDuration(null);
    }
  };


  useEffect(() => {
    if (!isEdit || !storePreviewUrl) {
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration)
        ? video.duration
        : null;

      setVideoDuration(duration);

      if (duration !== null) {
        setDuration(Math.floor(duration * 1000));
      }
    };

    video.src = storePreviewUrl;
  }, [isEdit, storePreviewUrl]);

  useEffect(() => {
    if (!videoDuration && duration) {
      setVideoDuration(Math.floor(duration / 1000));
    }
  }, []);


  const handleThumbnailSelect = (thumb: File) => {
    setThumbnail(thumb, URL.createObjectURL(thumb));
  };

  // ✅ Improved gallery upload validation
  const handleAddFromGallery = (thumb: File) => {
    setThumbnailError(null);
    if (!thumb.type.startsWith('image/')) {
      setThumbnailError('thumbnail_picker.errors.invalid_file_type');
      return;
    }
    if (thumb.size > thumbnailSizeLimit * 1024 * 1024) {
      setThumbnailError(t('thumbnail_picker.errors.file_size', { size: thumbnailSizeLimit }));
      return;
    }
    setThumbnail(thumb, URL.createObjectURL(thumb));
  };

  // ✅ Frame save from thumbnail picker
  const handleFrameSave = (thumb: string | File) => {
    if (typeof thumb === 'string') {
      // Data URL conversion
      const arr = thumb.split(',');
      const mime = /:(.*?);/.exec(arr[0])?.[1] || 'image/jpeg';
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.codePointAt(n) ?? 0;
      }
      setThumbnail(new File([u8arr], 'thumbnail.jpg', { type: mime }), thumb);
    } else {
      setThumbnail(thumb, URL.createObjectURL(thumb));
    }
  };

  // ✅ Validation schema with better error messages
  const schema = useMemo(() => {
    const requiredMessage = t('this_field_is_required', { defaultValue: 'This field is required' });
    const invalidTagsMessage = t('upload_video.errors.invalid_tags', {
      defaultValue: 'Invalid tags format. Tags must start with # and be separated by commas.',
    });
    return object().shape({
      title: string()
        .trim()
        .required(requiredMessage)
        .max(50, t('upload_video.errors.maxLength', { defaultValue: 'Max length exceeded', count: 50 })),
      description: string()
        .trim()
        .max(500, t('upload_video.errors.maxLength', { defaultValue: 'Max length exceeded', count: 500 }))
        .default(''),
      category: string().required(requiredMessage),
      tags: string()
        .trim()
        .required(requiredMessage)
        .test('is-valid-tags', invalidTagsMessage, (value) => {
          if (!value) {
            return true;
          }
          return isValidMediaTagsFormat(value);
        }),
    });
  }, [t]);

  const existingThumbnailKey = useMemo(() => {
    if (!thumbnailUrl) return null;
    const base = env.APP_BASE_URL || '';
    return base && thumbnailUrl.startsWith(base) ? thumbnailUrl.slice(base.length) : thumbnailUrl;
  }, [thumbnailUrl]);

  const isThumbnailChanged = !!thumbnail;

  // ✅ Enhanced submit handler
  const onSubmitHandler: UseFormOnSubmitHandler<UploadReelFormData> = async (formData, { setErrors, setSubmitting, validate, setValidationSchemaError }) => {
    setFileError(null);
    setThumbnailError(null);

    if (!isEdit && !file) {
      setFileError('upload_video.errors.select_video');
    }

    if (!validate(schema) || (!isEdit && !file)) {
      setSubmitting(false);
      setValidationSchemaError(true);
      return;
    }

    try {
      const mediaTags = parseUniqueMediaTags(formData.tags);

      if (isEdit && mediaId) {
        successRef.current = true
        await updateReel(
          mediaId,
          {
            maTitle: formData.title,
            maDescription: formData.description,
            maCategory: formData.category,
            maCategoryId: formData.category,
            maTags: mediaTags,
          },
          isThumbnailChanged ? thumbnail : null,   // only pass a File when it actually changed
          existingThumbnailKey,                     // fallback key when unchanged
        );
        // await updateMediaById({
        //   variables: {
        //     input: {
        //       maId: mediaId,
        //       maTitle: formData.title,
        //       maDescription: formData.description,
        //       maCategory: formData.category,
        //       maTags: mediaTags,
        //     }
        //   }
        // });
      } else if (file) {
        await uploadFile(file, {
          maTitle: formData.title,
          maDescription: formData.description,
          maType: 'VIDEOS',
          maCategory: formData.category,
          maTags: mediaTags,
          maCategoryId: formData.category,
          maOriginalFileName: file.name,
          maFileSize: String(file.size),
          ...(videoDuration !== null && { maDuration: String(Math.floor(videoDuration * 1000)) }),
        }, thumbnail || undefined);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('upload_video.errors.upload_failed');
      setErrors({ form: errorMessage });
    }

    setSubmitting(false);
  };

  const { handleSubmit, handleChange, handleBlur, values, errors, validationSchemaError, touched } = useForm<UploadReelFormData>({
    initialValues: storeValues,
    validationSchema: schema,
    validateOnBlur: true,
    onSubmit: onSubmitHandler,
  });

  useEffect(() => {
    if (isEdit && !baseValues && storeValues.title) {
      setBaseValues(storeValues);
    }
  }, [isEdit, baseValues, storeValues]);

  useEffect(() => {
    if (status === 'completed') {
      resetStore();
      if (successRef.current) {
        navigate(createURLFromLocation(location, { u: 'edit-success' }));
      } else {
        navigate(createURLFromLocation(location, { u: 'upload-success', message: 'video' }));
      }
    }
  }, [status, navigate, location, resetStore]);

  useEffect(() => {
    if (thumbnailError) {
      const timer = setTimeout(() => {
        setThumbnailError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [thumbnailError]);

  useEffect(() => {
    setValues(values);
  }, [values, setValues]);

  // ✅ Check if form data changed
  const isDirty = useMemo(() => {
    if (!isEdit || !baseValues) { return false; }
    return (
      values.title !== baseValues.title ||
      values.description !== baseValues.description ||
      values.category !== baseValues.category ||
      values.tags !== baseValues.tags ||
      isThumbnailChanged
    );
  }, [isEdit, values, baseValues, isThumbnailChanged]);

  const isFormValid = schema.isValidSync(values) && (isEdit ? isDirty : (!!file && !fileError));
  const isUploading = status === 'uploading' || updateLoading;

  const handleThumbnailExtracted = useCallback((thumb: File) => {
    // ✅ Only extract if file changed (different file, new thumbnail)
    if (file !== thumbnail) {
      const url = URL.createObjectURL(thumb);
      setThumbnail(thumb, url);
    }
  }, [file, thumbnail, setThumbnail]);

  useEffect(() => {
    if (isEdit && isDirty) {
      setUploadPopupConfirmation(true)
    } else {
      setUploadPopupConfirmation(false)
    }
  }, [isDirty, isEdit])

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{isEdit ? t('upload_video.edit_title', { defaultValue: 'Edit Video' }) : t('upload_video.title')}</h2>

      <div className={styles.errorContainer}>
        {errors.form ? <div className={styles.formError}>{errors.form}</div> : null}
        {uploadError ? <div className={styles.formError}>{uploadError}</div> : null}
      </div>

      <div className={styles.content}>
        <form id="upload-reel-form" onSubmit={handleSubmit} className={styles.formArea} noValidate>
          <UploadCard
            file={file}
            duration={duration}
            maxSizeMb={532}
            onFileSelect={handleFileChange}
            onThumbnailExtracted={handleThumbnailExtracted}
            disabled={isEdit}
            onPreview={() => {
              const backPath = `${location.pathname}${location.search}`;
              setReturnPath(backPath);
              navigate(`${PATH_VIDEO_PREVIEW}?type=video&returnPath=${encodeURIComponent(backPath)}`);
            }}
            label={'Video'}
            thumbnailUrl={thumbnailUrl}
          />

          {fileError ? <div className={styles.fileError}>{t(fileError)}</div> : null}
          {thumbnailError ? <div className={styles.fileError}>{thumbnailError}</div> : null}
          <TextField
            name="title"
            label={t('upload_video.form.title_label')}
            value={values.title}
            onChange={handleChange}
            onBlur={handleBlur}
            error={(validationSchemaError || touched.title !== undefined) && !!errors.title}
            helperText={validationSchemaError || touched.title !== undefined ? errors.title : ''}
            required
            maxLength={50}
            placeholder={t('upload_video.form.title_placeholder')}
            className={styles.lable}
          />

          <TextField
            name="description"
            label={t('upload_video.form.description_label')}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            error={(validationSchemaError || touched.description !== undefined) && !!errors.description}
            helperText={validationSchemaError || touched.description !== undefined ? errors.description : ''}
            multiline
            maxLength={500}
            showOptionalLabel={false}
            placeholder={t('upload_video.form.description_placeholder')}
            className={styles.lable}
          />

          <Dropdown
            name="category"
            label={t('upload_video.form.category_label')}
            value={values.category}
            onChange={handleChange as unknown as React.ChangeEventHandler<HTMLSelectElement>}
            onBlur={handleBlur as unknown as React.FocusEventHandler<HTMLSelectElement>}
            error={(validationSchemaError || touched.category !== undefined) && !!errors.category}
            helperText={validationSchemaError || touched.category !== undefined ? errors.category : ''}
            required
            defaultLabel={categoryLoading ? t('upload_video.form.category_loading', { defaultValue: 'Loading...' }) : t('upload_video.form.category_placeholder')}
            options={categories.map((cat: { ddId: string; ddLabel: string }) => ({
              value: cat.ddId,
              label: cat.ddLabel,
            }))}
            testId="category-dropdown"
            fullWidth
            className={styles.lable}
          />

          <TextField
            name="tags"
            label={t('upload_video.form.tags_label')}
            value={values.tags}
            onChange={handleChange}
            onBlur={handleBlur}
            error={(validationSchemaError || touched.tags !== undefined) && !!errors.tags}
            helperText={validationSchemaError || touched.tags !== undefined ? errors.tags : ''}
            required
            placeholder={t('upload_video.form.tags_placeholder')}
            className={styles.lable}
          />
        </form>
        <div className={styles.uploadColumn}>
          <label className={styles.coverImageLabel}>{t('upload_video.form.video_thumbnail')}</label>
          <UploadBox
            selectedFile={file}
            onFileSelect={handleFileChange}
            onThumbnailSelect={handleThumbnailSelect}
            onAddFromGallery={handleAddFromGallery}
            onEditCover={() => setIsThumbnailPickerOpen(true)}
            poster={thumbnailUrl || undefined}
            previewUrl={storePreviewUrl}
            hint={t('upload_box.max_size_hint')}
            error={!!fileError || !!thumbnailError}
            accept="video/*"
            height={146}
            className={styles.uploadArea}
            type="video"
            durationShown={false}
            actionsBelowPreview
            duration={videoDuration === null ? duration : Math.floor(videoDuration * 1000)}
            onPreview={() => {
              const backPath = `${location.pathname}${location.search}`;
              setReturnPath(backPath);
              navigate(`${PATH_VIDEO_PREVIEW}?returnPath=${encodeURIComponent(backPath)}`);
            }}
            disabled={false}
            isChannel={true}
          />
          {fileError ? <div className={styles.fileError}>{t(fileError)}</div> : null}
          {thumbnailError ? <div className={styles.fileError}>{thumbnailError}</div> : null}
        </div>


      </div>

      <div className={styles.submitContainer}>
        <Button
          form="upload-reel-form"
          type="submit"
          label={isEdit ? t('upload_video.form.save_changes') : t('upload_video.form.upload_button')}
          variant="contained"
          color="primary"
          disabled={isUploading || !isFormValid}
          className={styles.submitBtn}
        />
      </div>

      {isUploading && (
        <div className={styles.loadingOverlay}>
          <Spinner size="medium" />
          <div className={styles.uploadStatus}>Uploading...</div>
        </div>
      )}

      {status === 'failed' && (
        <div className={styles.loadingOverlay}>
          <img src={ErrorIcon} alt="error" />
          <div className={styles.uploadStatus} style={{ color: 'var(--alert-error)' }}>
            {t('upload_video.errors.upload_failed')}
          </div>
          <Button
            startIcon={<img src={RestartIcon} alt="restart" width={22} height={22} />}
            label={t('common.retry', { defaultValue: 'Try Again' })}
            onClick={resetUpload}
            variant="mediaOverlay"
            color="secondary"
          />
        </div>
      )}

      {/* ✅ Thumbnail picker - type="reel" forces 9:16 aspect ratio */}
      <ThumbnailUserPickerModal
        open={isThumbnailPickerOpen}
        onClose={() => setIsThumbnailPickerOpen(false)}
        videoFile={isEdit ? storePreviewUrl : file}
        onSave={handleFrameSave}
        initialThumbnail={thumbnailUrl || undefined}
        type='video'
      />
    </div>
  );
};

export default UploadVideoThumbnailModal;
